// چیدمانِ نمودار سنکی — منطقِ خالص، بدون DOM و بدون کتابخانه‌ی نمودار.
//
// ═══ چرا دستی و نه یک کتابخانه ═══
//
// قاعده‌ی مخزن: وابستگیِ تازه بدونِ تایید اضافه نمی‌شود. ولی دلیلِ بهترش این
// است که سنکیِ ما یک حالتِ خاص و ساده است — **لایه‌ای**، بدونِ چرخه و بدونِ
// بهینه‌سازیِ ترتیبِ گره‌ها. کلِ کارِ چیدمان همین صد خط است، در حالی که یک
// کتابخانه‌ی عمومی چند صد کیلوبایت و یک مدلِ ذهنیِ کامل می‌آورد.
//
// خروجی مختصاتِ خالص است؛ کشیدنش کارِ اپ است. همین باعث می‌شود این فایل با
// `npm test` آزمودنی باشد، بدونِ مرورگر.

/** یک گره: یک وضعیت در چرخه‌ی عمر. */
export interface SankeyNode {
  id: string;
  label: string;
  /** ستون. صفر یعنی سمتِ ورودی. */
  layer: number;
  /** «این‌جا کار تمام شد» — ریزش، نه ادامه‌ی مسیر. رنگش فرق می‌کند. */
  terminal?: boolean;
  /**
   * عددِ دومِ همین گره: «در آخرین اجرا». **فقط نمایشی** — چیدمان هرگز به
   * آن نگاه نمی‌کند و ارتفاعِ گره همچنان از `value` (پنجره‌ی ده روز) می‌آید.
   *
   * ⚠ اگر روزی چیدمان به این بند شود، نمودار روی اجرایی که فقط یک فاز
   * دویده تقریباً خالی می‌شود — همان دلیلی که نمودار از ستونِ ده روز
   * ساخته می‌شود و نه از یک اجرا (سرِ `CrawlerTab.tsx`).
   */
  run?: number;
  /** توضیحِ این گره برای کارتِ شناور. نمایشی؛ چیدمان کاری با آن ندارد. */
  why?: string;
}

export interface SankeyLink {
  from: string;
  to: string;
  value: number;
  /** برچسبِ گذر، مثل `T5`. از LIFECYCLE.md می‌آید. */
  label?: string;
}

export interface LaidOutNode extends SankeyNode {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

export interface LaidOutLink extends SankeyLink {
  /** مسیرِ SVG آماده. */
  path: string;
  /** ضخامت، متناسب با مقدار. */
  thickness: number;
  y0: number;
  y1: number;
}

export interface SankeyLayout {
  nodes: LaidOutNode[];
  links: LaidOutLink[];
  width: number;
  height: number;
}

export interface SankeyOptions {
  width?: number;
  height?: number;
  nodeWidth?: number;
  /** فاصله‌ی عمودیِ حداقلی بینِ دو گره در یک ستون. */
  nodePadding?: number;
  /** نازک‌ترین جریانِ قابلِ دیدن. بدونش جریانِ کوچک اصلاً دیده نمی‌شود. */
  minThickness?: number;
}

const DEFAULTS = {
  width: 900, height: 460, nodeWidth: 14, nodePadding: 14, minThickness: 1.5,
};

/**
 * مقدارِ هر گره = بیشترِ «مجموعِ ورودی» و «مجموعِ خروجی».
 *
 * چرا بیشتر و نه یکی از دو تا: گره‌های ابتدایی ورودی ندارند و گره‌های پایانی
 * خروجی. گرفتنِ ماکسیمم هر دو حالت را بدونِ شرطِ خاص پوشش می‌دهد.
 */
function nodeValues(nodes: SankeyNode[], links: SankeyLink[]): Map<string, number> {
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  for (const l of links) {
    outgoing.set(l.from, (outgoing.get(l.from) ?? 0) + l.value);
    incoming.set(l.to, (incoming.get(l.to) ?? 0) + l.value);
  }
  return new Map(nodes.map((n) => [
    n.id, Math.max(incoming.get(n.id) ?? 0, outgoing.get(n.id) ?? 0),
  ]));
}

/** منحنیِ افقیِ بزیه بین دو لبه — همان شکلی که هر سنکیِ دیگری دارد. */
const curve = (x0: number, y0: number, x1: number, y1: number): string => {
  const mid = (x0 + x1) / 2;
  return `M${x0},${y0} C${mid},${y0} ${mid},${y1} ${x1},${y1}`;
};

/**
 * گره‌ها و جریان‌ها را به مختصات تبدیل می‌کند.
 *
 * مقیاس **در هر ستون جداگانه** حساب می‌شود و نه یک‌بار برای کلِ نمودار.
 * دلیلش را یک بار به‌سختی فهمیدیم: ستونِ اول ۲۲۰۰ سند دارد و ستونِ آخر ۱۷۷۵
 * رخدادِ منتشرشده، ولی ستونِ میانی ۵۷۵۴ mention. با مقیاسِ مشترک، دو ستون
 * تقریباً نامرئی می‌شدند. با مقیاسِ ستونی، **نسبت‌های داخلِ هر مرحله** خوانا
 * می‌ماند — که همان چیزی است که آدم از سنکی می‌خواهد: «کجا ریخت».
 */
export function layoutSankey(
  nodes: SankeyNode[], links: SankeyLink[], opts: SankeyOptions = {},
): SankeyLayout {
  const o = { ...DEFAULTS, ...opts };
  const values = nodeValues(nodes, links);

  const layers = [...new Set(nodes.map((n) => n.layer))].sort((a, b) => a - b);
  const lastLayer = layers[layers.length - 1] ?? 0;
  const spanX = lastLayer > 0 ? (o.width - o.nodeWidth) / lastLayer : 0;

  const placed = new Map<string, LaidOutNode>();

  for (const layer of layers) {
    const inLayer = nodes.filter((n) => n.layer === layer);
    const total = inLayer.reduce((s, n) => s + (values.get(n.id) ?? 0), 0);
    const usable = o.height - o.nodePadding * Math.max(inLayer.length - 1, 0);
    // ستونِ تماماً صفر نباید تقسیم بر صفر بدهد؛ ارتفاعش را مساوی پخش می‌کنیم.
    const scale = total > 0 ? usable / total : 0;

    let y = 0;
    for (const n of inLayer) {
      const value = values.get(n.id) ?? 0;
      const height = total > 0
        ? Math.max(value * scale, 2)
        : usable / Math.max(inLayer.length, 1);
      placed.set(n.id, {
        ...n, value, x: layer * spanX, y, width: o.nodeWidth, height,
      });
      y += height + o.nodePadding;
    }
  }

  // جریان‌ها از بالای هر گره روی هم چیده می‌شوند تا هم‌پوشانی نداشته باشند.
  const outCursor = new Map<string, number>();
  const inCursor = new Map<string, number>();

  const laidLinks: LaidOutLink[] = links.map((l) => {
    const a = placed.get(l.from);
    const b = placed.get(l.to);
    if (!a || !b) throw new Error(`sankey: گرهِ ناشناخته در جریان ${l.from}→${l.to}`);

    const aTotal = Math.max(a.value, 1);
    const bTotal = Math.max(b.value, 1);
    const thickness = Math.max(
      Math.min((l.value / aTotal) * a.height, (l.value / bTotal) * b.height),
      o.minThickness,
    );

    const oy = outCursor.get(l.from) ?? 0;
    const iy = inCursor.get(l.to) ?? 0;
    const y0 = a.y + oy + thickness / 2;
    const y1 = b.y + iy + thickness / 2;
    outCursor.set(l.from, oy + thickness);
    inCursor.set(l.to, iy + thickness);

    return {
      ...l, thickness, y0, y1,
      path: curve(a.x + a.width, y0, b.x, y1),
    };
  });

  return {
    nodes: [...placed.values()], links: laidLinks, width: o.width, height: o.height,
  };
}
